import type { PersonaAnswers, PersonaRoute } from '@secretario/shared';
import { useState } from 'react';
import { Link, useLocation } from 'wouter';
import { describeError } from '../lib/api/http.js';
import { compilePersona, previewPersona } from '../lib/api/personal.js';
import { characterImage, DEFAULT_CHARACTER, type Expression } from '../lib/characters.js';
import { PROFILE_KEY, useProfile } from '../lib/useProfile.js';
import { useQueryClient } from '@tanstack/react-query';
import './ported/jeito-unico.css';
import { BackIcon } from '../components/chrome/BackIcon.js';

type StepId =
  'about' | 'speechStyle' | 'tones' | 'boundaries' | 'routes' | 'neverPhrases' | 'signoff';

const STEPS: { id: StepId; expr: Expression; kicker: string; title: string; sub: string }[] = [
  {
    id: 'about',
    expr: 'simpatico',
    kicker: '1 de 7',
    title: 'Conta sobre você',
    sub: 'Profissão, rotina, o que é relevante pra ele saber sobre você pra conversar com naturalidade.',
  },
  {
    id: 'speechStyle',
    expr: 'divertido',
    kicker: '2 de 7',
    title: 'Como você fala',
    sub: 'Bordões, gírias, emojis que você usa, ele vai puxar esse jeito.',
  },
  {
    id: 'tones',
    expr: 'elegante',
    kicker: '3 de 7',
    title: 'O tom com cada grupo',
    sub: 'Com família, com trabalho, com clientes: o jeito muda?',
  },
  {
    id: 'boundaries',
    expr: 'direto',
    kicker: '4 de 7',
    title: 'O que pode e o que não pode',
    sub: 'Assuntos livres pra responder sozinho, e os que precisam sempre passar por você.',
  },
  {
    id: 'routes',
    expr: 'tranquilo',
    kicker: '5 de 7',
    title: 'Suas rotas',
    sub: 'Regras do tipo "se pedirem X, faça Y", até 10.',
  },
  {
    id: 'neverPhrases',
    expr: 'espontaneo',
    kicker: '6 de 7',
    title: 'Frases que você nunca diria',
    sub: 'Pra ele nunca soar diferente de você.',
  },
  {
    id: 'signoff',
    expr: 'simpatico',
    kicker: '7 de 7',
    title: 'Sua despedida',
    sub: 'Como você costuma fechar uma conversa (se tiver um jeito característico).',
  },
];

const CHIP_SUGGESTIONS: Partial<Record<StepId, string[]>> = {
  speechStyle: [
    'Uso "cara" e "mano"',
    'Gosto de emoji 😄',
    'Sou direto, sem enrolação',
    'Falo bem formal',
  ],
  neverPhrases: ['"Prezado(a)"', 'Gírias forçadas', 'Emoji em excesso', 'Respostas longas demais'],
};

type TextField = Exclude<keyof PersonaAnswers, 'routes'>;

export function JeitoUnicoPage() {
  const [, navigate] = useLocation();
  const qc = useQueryClient();
  const profileQ = useProfile();

  const characterKey = profileQ.data?.characterKey ?? DEFAULT_CHARACTER;
  const agentName = profileQ.data?.secretaryName?.trim() || 'Seu secretário';

  const existingPersona = (profileQ.data?.preferences.persona ?? {}) as PersonaAnswers;
  const existingPrompt = profileQ.data?.preferences.personaPrompt;
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState<PersonaAnswers>(existingPersona);
  const [routes, setRoutes] = useState<PersonaRoute[]>(
    existingPersona.routes?.length ? existingPersona.routes : [{ trigger: '', action: '' }],
  );
  const [compiling, setCompiling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(
    typeof existingPrompt === 'string' && existingPrompt ? existingPrompt : null,
  );

  const [testMessage, setTestMessage] = useState('');
  const [testing, setTesting] = useState(false);
  const [testReply, setTestReply] = useState<string | null>(null);

  const step = STEPS[stepIdx]!;
  const isLast = stepIdx === STEPS.length - 1;
  const heroImg = characterImage(characterKey, step.expr);

  function setField(id: TextField, value: string) {
    setAnswers((prev) => ({ ...prev, [id]: value }));
  }
  function updateRoute(i: number, patch: Partial<PersonaRoute>) {
    setRoutes((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }
  function addRoute() {
    setRoutes((prev) => (prev.length >= 10 ? prev : [...prev, { trigger: '', action: '' }]));
  }
  function removeRoute(i: number) {
    setRoutes((prev) => prev.filter((_, idx) => idx !== i));
  }

  async function finish() {
    setCompiling(true);
    setError(null);
    try {
      const fullAnswers: PersonaAnswers = {
        ...answers,
        routes: routes.filter((r) => r.trigger.trim() && r.action.trim()),
      };
      const { personaPrompt } = await compilePersona(fullAnswers);
      await qc.invalidateQueries({ queryKey: PROFILE_KEY });
      setResult(personaPrompt);
    } catch {
      setError('Não consegui salvar agora. Tente de novo em alguns segundos.');
    } finally {
      setCompiling(false);
    }
  }

  async function runPreview() {
    if (!result || !testMessage.trim()) return;
    setTesting(true);
    setTestReply(null);
    try {
      const { reply } = await previewPersona(result, testMessage.trim());
      setTestReply(reply);
    } catch (err) {
      setTestReply(
        describeError(err, 'Não consegui testar agora, mas o jeito único já foi salvo.'),
      );
    } finally {
      setTesting(false);
    }
  }

  const chips = CHIP_SUGGESTIONS[step.id];
  const text = (id: TextField) => answers[id] ?? '';

  return (
    <div className="ju">
      <div className="ju-wrap">
        <header className="ju-hd">
          <Link href="/" className="ju-back back-btn" aria-label="Voltar para a tela inicial">
            <BackIcon />
          </Link>
        </header>

        {!result ? (
          <>
            <div className="ju-hero">
              <img src={heroImg} alt={agentName} />
            </div>

            <div className="ju-progress">
              {STEPS.map((s, i) => (
                <span key={s.id} className={i <= stepIdx ? 'on' : ''} />
              ))}
            </div>

            <div className="ju-kicker">{step.kicker} · Jeito único</div>
            <h1 className="ju-h1">{step.title}</h1>
            <p className="ju-sub">{step.sub}</p>

            {step.id === 'about' && (
              <textarea
                className="ju-input"
                rows={4}
                value={text('about')}
                onChange={(e) => setField('about', e.target.value)}
                placeholder="Ex.: sou dentista, atendo de manhã, à tarde costumo estar em cirurgia…"
              />
            )}
            {step.id === 'speechStyle' && (
              <textarea
                className="ju-input"
                rows={4}
                value={text('speechStyle')}
                onChange={(e) => setField('speechStyle', e.target.value)}
                placeholder="Ex.: falo bem direto, gosto de emoji, chamo todo mundo de 'cara'…"
              />
            )}
            {step.id === 'tones' && (
              <div className="ju-multi">
                <label>Com a família</label>
                <textarea
                  className="ju-input"
                  rows={2}
                  value={text('familyTone')}
                  onChange={(e) => setField('familyTone', e.target.value)}
                  placeholder="Ex.: carinhoso, informal"
                />
                <label>Com o trabalho</label>
                <textarea
                  className="ju-input"
                  rows={2}
                  value={text('workTone')}
                  onChange={(e) => setField('workTone', e.target.value)}
                  placeholder="Ex.: objetivo, sem enrolação"
                />
                <label>Com clientes</label>
                <textarea
                  className="ju-input"
                  rows={2}
                  value={text('clientTone')}
                  onChange={(e) => setField('clientTone', e.target.value)}
                  placeholder="Ex.: cordial, nunca promete nada sem confirmar"
                />
              </div>
            )}
            {step.id === 'boundaries' && (
              <div className="ju-multi">
                <label>Pode falar sozinho sobre</label>
                <textarea
                  className="ju-input"
                  rows={2}
                  value={text('canSay')}
                  onChange={(e) => setField('canSay', e.target.value)}
                  placeholder="Ex.: horários, endereço, preços já combinados"
                />
                <label>NUNCA pode falar sozinho sobre</label>
                <textarea
                  className="ju-input"
                  rows={2}
                  value={text('cannotSay')}
                  onChange={(e) => setField('cannotSay', e.target.value)}
                  placeholder="Ex.: dinheiro, saúde, briga, sempre me chama"
                />
              </div>
            )}
            {step.id === 'routes' && (
              <div className="ju-routes">
                {routes.map((r, i) => (
                  <div className="ju-route" key={i}>
                    <span className="ju-route-if">Se</span>
                    <input
                      value={r.trigger}
                      onChange={(e) => updateRoute(i, { trigger: e.target.value })}
                      placeholder="pedirem desconto"
                    />
                    <span className="ju-route-then">→</span>
                    <input
                      value={r.action}
                      onChange={(e) => updateRoute(i, { action: e.target.value })}
                      placeholder="diz que só você decide"
                    />
                    {routes.length > 1 && (
                      <button
                        type="button"
                        className="ju-route-rm"
                        onClick={() => removeRoute(i)}
                        aria-label="Remover"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
                {routes.length < 10 && (
                  <button type="button" className="ju-route-add" onClick={addRoute}>
                    + Adicionar rota
                  </button>
                )}
              </div>
            )}
            {step.id === 'neverPhrases' && (
              <textarea
                className="ju-input"
                rows={4}
                value={text('neverPhrases')}
                onChange={(e) => setField('neverPhrases', e.target.value)}
                placeholder="Ex.: nunca uso 'prezado', nunca respondo com só um emoji…"
              />
            )}
            {step.id === 'signoff' && (
              <textarea
                className="ju-input"
                rows={3}
                value={text('signoff')}
                onChange={(e) => setField('signoff', e.target.value)}
                placeholder="Ex.: sempre fecho com 'abraço!' ou 'bjs'…"
              />
            )}

            {chips && (
              <div className="ju-chips">
                {chips.map((c) => {
                  const key = step.id as TextField;
                  const current = text(key);
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setField(key, current ? `${current}, ${c}` : c)}
                    >
                      {c}
                    </button>
                  );
                })}
              </div>
            )}

            <div className="ju-nav">
              {stepIdx > 0 && (
                <button
                  type="button"
                  className="ju-back-btn"
                  onClick={() => setStepIdx((i) => i - 1)}
                >
                  Voltar
                </button>
              )}
              {!isLast ? (
                <button type="button" className="ju-next" onClick={() => setStepIdx((i) => i + 1)}>
                  Próximo →
                </button>
              ) : (
                <button
                  type="button"
                  className="ju-next"
                  onClick={() => void finish()}
                  disabled={compiling}
                >
                  {compiling ? 'Salvando…' : 'Salvar meu jeito único'}
                </button>
              )}
            </div>
            {error && <p className="ju-error">{error}</p>}
            <button type="button" className="ju-skip" onClick={() => navigate('/')}>
              Pular por agora
            </button>
          </>
        ) : (
          <div className="ju-done">
            <div className="ju-hero small">
              <img src={characterImage(characterKey, 'divertido')} alt={agentName} />
            </div>
            <div className="ju-kicker">Pronto!</div>
            <h1 className="ju-h1">{agentName} agora tem a sua cara</h1>
            <p className="ju-sub">
              Testa abaixo como ele responderia a uma mensagem, e ajusta se quiser.
            </p>

            <div className="ju-preview-box">
              <label>Mensagem de teste de um contato</label>
              <div className="ju-testrow">
                <input
                  value={testMessage}
                  onChange={(e) => setTestMessage(e.target.value)}
                  placeholder="Ex.: oi, tudo bem? Bora marcar aquele café?"
                />
                <button
                  type="button"
                  onClick={() => void runPreview()}
                  disabled={testing || !testMessage.trim()}
                >
                  {testing ? '…' : 'Testar'}
                </button>
              </div>
              {testReply && (
                <div className="ju-testreply">
                  <span className="ju-testreply-av">
                    <img src={characterImage(characterKey, 'simpatico')} alt="" />
                  </span>
                  <p>{testReply}</p>
                </div>
              )}
            </div>

            <div className="ju-navdone">
              <button type="button" className="ju-back-btn" onClick={() => setResult(null)}>
                Ajustar respostas
              </button>
              <button type="button" className="ju-next" onClick={() => navigate('/')}>
                Concluir
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
